import {useMsal} from '@azure/msal-react';
import {getToken, useLogin} from '../../authConfig';
import { DomainOption } from "./DomainOption";
import React, { useState, useEffect } from "react";
import { useTrail, a } from "@react-spring/web";
import { getDomainList, DomainListResponse } from "../../api";

import styles from "./DomainOption.module.css";

interface Props {
    onDomainOptionClicked: (value: string) => void;
    setSnackbar: (snackbar: { children: string; severity: "success" | "error" }) => void;
    onDomainsLoaded?: (canLoad: boolean) => void;
}

export const DomainList = ({ onDomainOptionClicked, setSnackbar, onDomainsLoaded}: Props) => {
    const client = useLogin ? useMsal().instance : undefined;
    const [domains, setDomains] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const fetchDomains = async () => {
        try {
            setLoading(true);
            const token = client ? await getToken(client) : undefined;
            const response = await getDomainList(token);
            const data = await response.json() as DomainListResponse;

            // Filter for active domains only and extract domain names
            const activeDomains = data.domains.sort(
                (a, b) => parseInt(a.domain_id) - parseInt(b.domain_id)
            )
            .filter(domain => domain.active_status)
            .map(domain => domain.domain_name);

            // If no active domains are available, display an error message
            if (activeDomains.length === 0) {
                console.error("No active domains available.");
                setSnackbar({children: "Error: no domains available.", severity: "error"});
                setDomains([]);
            }
            // If only one domain is available, set it as the default
            else if (activeDomains.length === 1) {
                const singleDomain = activeDomains[0];
                onDomainOptionClicked(singleDomain);
                setDomains(activeDomains);
            }
            else {
                setDomains(activeDomains);
            }

        } catch (error) {
            console.error("Error fetching domain list.", error);
            setSnackbar({children: "Error fetching list of available domains.", severity: "error"});
            setDomains([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDomains();
    }, []);

    useEffect(() => {
        onDomainsLoaded?.(domains.length > 1);
    }, [domains, onDomainsLoaded]);

    if (loading) {
        return <div className={styles.domainLoadingMsg}><i>Loading active domains...</i></div>;
    }


    return (
        <div>
            <ul className={styles.domainOptionsNavList}>
            {domains.length > 1 && domains.map((domain, i) => (
                <li key={i}>
                    <DomainOption text={domain} value={domain} onClick={onDomainOptionClicked} />
                </li>
            ))}
            </ul>
            {domains.length===0 && <div><i>Error: You do not appear to have access to any domains, or none are currently active.</i></div>}
        </div>

    );
};


// TODO: fix bug - animation plays when any part of the domain list component is clicked, even outside of the domain options
const DomainListFadeOutTrail: React.FC<{ children: React.ReactNode, open: boolean }> = ({ open, children }) => {
  const items = React.Children.toArray(children)
  const trail = useTrail(items.length, {
    config: { mass: 5, tension: 2000, friction: 200 },
    opacity: open ? 1 : 0,
    x: open ? 0 : 20,
    height: open ? 110 : 0,
    from: { opacity: 0, x: 20, height: 0 },
  })
  return (
    <div>
      {trail.map(({ height, ...style }, index) => (
        <a.div key={index} className={styles.trailsText} style={style}>
          <a.div>{items[index]}</a.div>
        </a.div>
      ))}
    </div>
  )
}

export function DomainListAnimated({ onDomainOptionClicked, setSnackbar, onDomainsLoaded}: Props) {
  const [open, set] = useState(true)
  return (
    <div className={styles.container} onClick={() => {set(state => !state); setTimeout(() => set(state => !state), 1000); }}>
      <DomainListFadeOutTrail open={open}>
        <DomainList onDomainOptionClicked={onDomainOptionClicked} setSnackbar={setSnackbar} onDomainsLoaded={onDomainsLoaded}></DomainList>
      </DomainListFadeOutTrail>
    </div>
  )
}
